import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { IContact, INamedStringObject, IObject } from '@type_definitions/datatypes';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { BPProfilesContactsDataService, IRole } from './bp-profiles-contacts-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import _ from 'underscore';
import { CellClassParams, GetRowIdParams, GridOptions, ICellRendererParams, RowNode } from 'ag-grid-community';
import { IColDef } from '@app/shared/report/report-grid.service';
import { BPProfilesAccessSettingsDataService } from '../access-settings/bp-profiles-access-settings-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { IAgContactInfoRendererParams } from '@app/shared/ag-grid-components/ag-contact-info/ag-contact-info-renderer.component';
import { IAgButtonDropdownRendererParams } from '@app/shared/ag-grid-components/ag-button-dropdown/ag-button-dropdown-renderer.component';
import { SendMailService } from '@app/shared/send-mail/send-mail.service';
import { DialogBusyButtonFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '@app/shared/message/ga-message.service';
import { EditContactInfoDialogComponent } from './edit-contact-info-dialog.component';
import { ContactRoleDialogComponent, ContactRoleDialogMode } from './contact-role-dialog.component';
import { IAgButtonRendererParams } from '@app/shared/ag-grid-components/ag-button/ag-button-renderer.component';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { util } from '@app/shared/util';
import { AgGridDomLayout, GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { BUTTON_CANCEL, BUTTON_DELETE } from '@app/shared/message/dialogs/default-form-dialog.component';
import { GRID_BP_CONTACTS } from '@app/shared/app-grids';

interface IContactRoleTab extends INamedStringObject {
  reportId?:number;
  filters?:IObject;
}

enum GridColumnKey {
  OPTIONS = 'options',
  ROLE = 'role',
  NAME = 'name',
  COMPANY = 'company',
  PHONE = 'phone',
  LEVEL = 'level',
  OEM = 'oem',
  MAIN_CONTACT = 'mainContact'
}

export enum ContactRoleTab {
  ACTIVE_ROLES = 'ACTIVE_ROLES',
  MISSING_ROLES = 'MISSING_ROLES',
  OEM_ROLES = 'OEM_ROLES',
  WEBSHOP_ROLES = 'WEBSHOP_ROLES',
  ALL_CONTACTS = 'ALL_CONTACTS'
}

interface IRoleGridData {
  role:string;
  company:string;
  phone:string;
  level:string;
  oem:string;
  mainContact:string;
  roleData:IRole;
}

const gridHeightOffset:string = '30px;.ui-layout-north;.ui-layout-resizer-north;.role-selector;.fixed-to-top-container;.new-role-button;.report-wrapper>h3';

@Component({
  selector: 'bp-profiles-contacts',
  template: `
    <div class="bp-profiles-contacts">
      <div class="btn-group role-selector margin-bottom-sm" *ngIf="!hideRoleSelector">
        <button *ngFor="let role of roleTabs" class="btn" [class.active]="selectedRoleTab?.id === role.id" (click)="changeContactRoleTab(role)">{{ role.name }}</button>
      </div>

      <div class="new-role-button" *ngIf="hasWritePermission && !selectedRoleTab?.reportId && !disableActions">
        <button class="btn btn-primary" translate [translateParams]="{ label: 'Role' }" (click)="onShowRoleDialog('${ContactRoleDialogMode.NEW}')">GA.BUTTONS.NEW_LABEL</button>
      </div>

      <div *ngIf="!selectedRoleTab?.reportId">
        <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" [height]="400" [fillHeight]="gridHeightOffset"></ga-ag-grid>
      </div>

      <div *ngIf="selectedRoleTab?.reportId">
        <report-wrapper
          [reportId]="selectedRoleTab.reportId"
          [filters]="selectedRoleTab.filters"
          [gridHeight]="gridHeightOffset">
        </report-wrapper>
      </div>
    </div>
  `,
  styleUrls: ['./bp-profiles-contacts.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesContactsComponent implements OnInit {
  @Input() public selectedRoleTab:IContactRoleTab;
  @Input() public hideRoleSelector:boolean;
  @Input() public gridAutoHeight:boolean;
  @Input() public disableActions:boolean;
  @Input() public companyId:number;
  @Input() public vendorCompanyId:number;
  @Input() public hideOptionsColumn:boolean;

  public gridId:string = GRID_BP_CONTACTS;
  public roleTabs:IContactRoleTab[];
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public hasWritePermission:boolean;
  public gridHeightOffset:string;

  private originalRoleData:IRole[];

  constructor(
    private translationService:TranslationService,
    private bpProfilesService:BPProfilesService,
    private bpProfilesAccessSettingsDataService:BPProfilesAccessSettingsDataService,
    private formattingService:FormattingService,
    private sendMailService:SendMailService,
    private messageService:GaMessageService,
    private bpProfilesContactsDataService:BPProfilesContactsDataService,
    private deferredService:GaDeferredService,
    private authService:AuthService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.CONTACTS);
    this.roleTabs = this.createRoleTabs();
    // set default tab
    this.selectedRoleTab = this.selectedRoleTab || _.findWhere(this.roleTabs, { id: ContactRoleTab.ACTIVE_ROLES });
    this.gridHeightOffset = !this.gridAutoHeight ? gridHeightOffset : undefined;
    // initialize grid
    this.gridInitialize();
  }

  public changeContactRoleTab(selectedRoleTab:IContactRoleTab):Promise<IRole[]> {
    const deferred:IGADeferred<IRole[]> = this.deferredService.defer();
    this.selectedRoleTab = selectedRoleTab;

    // we only load necessary data when report id is not defined in selected role tab
    // because data will be loaded by embedded-report component
    if (!selectedRoleTab.reportId) {
      this.gridOptions.context.selectedRoleTab = selectedRoleTab?.id;
      // add timeout because rendering might still in progress
      setTimeout(() => {
        this.gaAgGridService.setColumnsVisible(this.gridId, [GridColumnKey.OPTIONS], this.selectedRoleTab.id === ContactRoleTab.ACTIVE_ROLES);
        this.loadData()
          .then(deferred.resolve)
          .catch(deferred.reject);
      });
    }

    return deferred.promise;
  }

  private async loadData():Promise<IRole[]> {
    this.gaAgGridService.setGridLoading(this.gridId, true);
    return this.bpProfilesAccessSettingsDataService.getCompanyRoles(this.bpProfilesService.activeProfile?.id, this.companyId || this.bpProfilesService.activeProfile?.company?.id, this.vendorCompanyId)
      .then(this.processRoles)
      .finally(() => {
        this.gaAgGridService.setGridLoading(this.gridId, false);
      });
  }

  private processRoles = (roles:IRole[]):Promise<IRole[]> => {
    this.originalRoleData = roles;
    const data:IRoleGridData[] = [];

    _.chain(roles)
      .filter((role:IRole) => {
        switch(this.selectedRoleTab?.id) {
          case ContactRoleTab.ACTIVE_ROLES:
            return !_.isUndefined(role.contact);
          case ContactRoleTab.MISSING_ROLES:
            return _.isUndefined(role.contact);
          default:
            return true;
        }
      })
      .each((role:IRole) => {
        data.push({
          [GridColumnKey.ROLE]: this.formattingService.formatContactType(role?.contactType),
          [GridColumnKey.COMPANY]: this.formattingService.formatCompany(role?.contactCompany),
          [GridColumnKey.PHONE]: this.formattingService.formatPhone(role?.contact),
          [GridColumnKey.LEVEL]: this.formattingService.formatCountry(role?.country),
          [GridColumnKey.OEM]: this.formattingService.formatCompany(role?.vendor),
          [GridColumnKey.MAIN_CONTACT]: role?.mainContact ? this.translationService.instant('GA.GENERAL.YES') : '',
          roleData: role
      });
    });

    this.gaAgGridService.setRowData(this.gridId, data);
    return Promise.resolve(roles);
  };

  private getColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumnKey.OPTIONS,
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        cellClass: 'cell-actions text-center ag-button-dropdown-renderer',
        cellRenderer: CellRendererComp.BUTTON_DROPDOWN_RENDERER,
        cellRendererParams: <IAgButtonDropdownRendererParams>{
          dropdownMenus: [
            { label: 'BPPROFILES.CONTACTS.OPTIONS.SEND_MAIL', onClick: this.onSendMail, iconClass: 'fa-envelope-o' },
            { label: 'BPPROFILES.CONTACTS.OPTIONS.EDIT_CONTACT_INFO', onClick: this.onEditContactInfo, iconClass: 'fa-pencil', isHidden: !this.hasWritePermission },
            { label: 'BPPROFILES.CONTACTS.OPTIONS.EDIT_ROLE', onClick: (params:IAgButtonDropdownRendererParams) => this.onShowRoleDialog(ContactRoleDialogMode.EDIT, params), iconClass: 'fa-pencil', isHidden: !this.hasWritePermission },
            { label: 'BPPROFILES.CONTACTS.OPTIONS.DELETE_ROLE', onClick: this.onDeleteRole, iconClass: 'fa-trash-o', isHidden: !this.hasWritePermission }
          ]
        },
        suppressMovable: true,
        suppressAutoSize: true,
        suppressColumnsToolPanel: true
      },
      {
        field: GridColumnKey.ROLE,
        headerName: 'BPPROFILES.CONTACTS.GRID.ROLE'
      },
      {
        field: GridColumnKey.NAME,
        headerName: 'BPPROFILES.CONTACTS.GRID.NAME',
        cellClass: (params:CellClassParams):string => {
          return params.context.selectedRoleTab === ContactRoleTab.ACTIVE_ROLES ? '' : 'text-center';
        },
        cellRendererSelector: (params:ICellRendererParams) => {
          if (util.notNullOrUndefinedOrEmpty(params.data?.roleData?.contact)) {
            return {
              component: CellRendererComp.CONTACT_INFO_RENDERER,
              params: {
                getContact: (params:IAgContactInfoRendererParams):IContact => {
                  return params.node.data?.roleData?.contact;
                }
              }
            };
          } else {
            // we only show add contact button when user has 'write' permission and disabledActions are false
            if (this.hasWritePermission && !this.disableActions && util.isNullOrUndefinedOrEmpty(params.data?.roleData?.contact)) {
              return {
                component: CellRendererComp.BUTTON_RENDERER,
                params: {
                  label: this.translationService.instant('GA.BUTTONS.ADD_LABEL', { label: 'BPPROFILES.CONTACTS.CONTACT.LABEL' }),
                  onClick: (params:IAgButtonRendererParams) => this.onShowRoleDialog(ContactRoleDialogMode.NEW, params),
                  className: 'btn-primary btn-add-role-contact'
                }
              };
            }

            return {};
          }
        }
      },
      {
        field: GridColumnKey.COMPANY,
        headerName: 'BPPROFILES.CONTACTS.GRID.COMPANY'
      },
      {
        field: GridColumnKey.PHONE,
        headerName: 'BPPROFILES.CONTACTS.GRID.PHONE'
      },
      {
        field: GridColumnKey.LEVEL,
        headerName: 'BPPROFILES.CONTACTS.GRID.LEVEL',
        cellClass: 'text-center'
      },
      {
        field: GridColumnKey.OEM,
        headerName: 'BPPROFILES.CONTACTS.GRID.OEM'
      },
      {
        field: GridColumnKey.MAIN_CONTACT,
        headerName: 'BPPROFILES.CONTACTS.GRID.MAIN_CONTACT',
        cellClass: 'text-center',
        cellRenderer: (params:ICellRendererParams) => {
          if (params.value === this.translationService.instant('GA.GENERAL.YES')) {
            return '<i class="fa fa-check table-ok-icon ok"></i>';
          }

          return '';
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);

      if (colDef.field !== GridColumnKey.OPTIONS) {
        colDef.resizable = true;
        colDef.sortable = true;
      }
    });
    
    if (this.hideOptionsColumn) {
      return _.reject(colDefs, (colDef:IColDef) => colDef.field === GridColumnKey.OPTIONS);
    }

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.getColDefs(),
      onGridReady: ():void => {
        this.loadData();
      },
      context: {
        selectedRoleTab: this.selectedRoleTab?.id
      },
      getRowId: (row:GetRowIdParams<IRoleGridData>) => {
        const roleId:number = row.data.roleData?.id;
        
        if (!_.isUndefined(roleId)) {
          return roleId.toString();
        }
        
        return `${row.data.roleData?.contactCompany?.id}-${row.data.roleData?.contactType?.code}`;
      },
      domLayout: this.gridAutoHeight ? AgGridDomLayout.AUTO_HEIGHT : undefined
    };

    this.gridInit = true;
  }

  private createRoleTabs():IContactRoleTab[] {
    return [
      {
        id: ContactRoleTab.ACTIVE_ROLES,
        name: this.translationService.instant('BPPROFILES.CONTACTS.TABS.ACTIVE')
      },
      {
        id: ContactRoleTab.MISSING_ROLES,
        name: this.translationService.instant('BPPROFILES.CONTACTS.TABS.MISSING')
      },
      {
        id: ContactRoleTab.WEBSHOP_ROLES,
        name: this.translationService.instant('BPPROFILES.CONTACTS.TABS.WEBSHOP'),
        reportId: 68,
        filters: {
          Decomnm: this.bpProfilesService.activeProfile.company?.id,
          Delevel: '0,1'
        }
      },
      {
        id: ContactRoleTab.ALL_CONTACTS,
        name: this.translationService.instant('BPPROFILES.CONTACTS.TABS.ALL'),
        reportId: 31,
        filters: {
          Decomnm: this.bpProfilesService.activeProfile.company?.id
        }
      }
    ];
  }

  private getRolesDataFromRows():IRole[] {
    // we use this method instead 'originalRoleData' because we only show results based on what displayed in the grid
    // 'originalRoleData' contains missing roles as well
    const data:IRole[] = [];
    this.gaAgGridService.forEachNode(this.gridId, (rowNode:RowNode) => {
      data.push(rowNode.data.roleData);
    });
    return data;
  }

  private onSendMail = (params:IAgButtonDropdownRendererParams):void => {
    this.sendMailService.showSendMailDialog(undefined, undefined, [ (<IRole>params.data?.roleData)?.contact ]);
  };

  private onEditContactInfo = (params:IAgButtonDropdownRendererParams):void => {
    const dialog:IDialog = this.messageService.showDialog({
      component: EditContactInfoDialogComponent,
      inputs: {
        model: params.data?.roleData?.contact,
        title: 'BPPROFILES.CONTACTS.OPTIONS.EDIT_CONTACT_INFO',
        profileId: this.bpProfilesService.activeProfile?.id,
        bpCompanyId: this.bpProfilesService.activeProfile?.company?.id,
        entity: this.translationService.instant('BPPROFILES.CONTACTS.CONTACT.LABEL')
      },
      outputs: {
        onSave: (newContact:IContact):void => {
          // after successfully saved then update with the latest contact info
          const data:IRole[] = this.getRolesDataFromRows();
          data[params.node.rowIndex].contact = newContact;
          this.processRoles(data);

          // close dialog
          dialog.hide();
        },
        onCancel: ():void => dialog.hide()
      },
      wrapperComponent: false,
      size: DialogSize.MD
    });
  };

  public onShowRoleDialog = (dialogMode:string, params?:ICellRendererParams):void => {
    const dialog:IDialog = this.messageService.showDialog({
      component: ContactRoleDialogComponent,
      inputs: {
        mode: dialogMode,
        model: util.deepClone(params?.data?.roleData) || {},
        title: dialogMode === ContactRoleDialogMode.EDIT ? this.translationService.instant('GA.BUTTONS.EDIT_LABEL', { label: 'Role' }) : this.translationService.instant('GA.BUTTONS.NEW_LABEL', { label: 'Role'}),
        profileId: this.bpProfilesService.activeProfile?.id,
        bpCompanyId: this.bpProfilesService.activeProfile?.company?.id,
        entity: this.translationService.instant('BPPROFILES.CONTACTS.GRID.ROLE'),
        country: this.bpProfilesService.activeProfile?.country,
        roleData: this.originalRoleData
      },
      outputs: {
        onSave: (savedRole:IRole):void => {
          // when the selected tab is not active roles then go to active role tab and reload all data from server
          this.changeContactRoleTab(_.findWhere(this.createRoleTabs(), { id: ContactRoleTab.ACTIVE_ROLES }))
            .then(() => {
              // after data loaded and tab change to active then we highlight row which successfully saved
              this.gaAgGridService.highlightGridRow(this.gridId, savedRole.id);
            });
          // close dialog
          dialog.hide();
        },
        onCancel: ():void => dialog.hide()
      },
      wrapperComponent: false,
      size: DialogSize.MD
    });
  };

  private onDeleteRole = (params:IAgButtonDropdownRendererParams):void => {
    const selectedRole:IRole = params.data.roleData;

    const dialogButtons:IDialogButton[] = [
      {
        id: BUTTON_DELETE,
        label: 'GA.BUTTONS.DELETE',
        onClick: (updateBusyButtonStatus:DialogBusyButtonFn) => {
          updateBusyButtonStatus(true);
          this.bpProfilesContactsDataService.deleteContactRole(this.bpProfilesService.activeProfile?.id, selectedRole.id, this.bpProfilesService.activeProfile?.company?.id)
            .then(() => {
              const data:IRole[] = this.getRolesDataFromRows();
              data.splice(params.node.rowIndex, 1);
              this.processRoles(data);
              // close dialog
              dialog.hide();
            })
            .finally(() => {
              updateBusyButtonStatus(false);
            });
        },
        className: 'btn-danger'
      },
      { id: BUTTON_CANCEL, label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }), onClick: () => dialog.hide(), className: 'btn-success' }
    ];

    const dialog:IDialog = this.messageService.showMessageDialog(
      'GA.GENERAL.CONFIRM_DELETE',
      this.translationService.instant('BPPROFILES.CONTACTS.CONFIRM_DELETE_ROLE', { role: params.data.role, fullName: selectedRole.contact.fullName }),
      dialogButtons,
      undefined,
      undefined,
      DialogSize.MD
    );
  };
}